import { Compass, MessageCircle, Lock, Quote } from "lucide-react";
import communityImage from "@/assets/student-community.jpg";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { useScaleParallax } from "@/hooks/use-parallax";

interface ReasonCardProps {
  icon: React.ElementType;
  color: string;
  title: string;
  description: {
    full: string;
    mobile: string;
  };
  index: number;
}

const ReasonCard = ({ icon: Icon, color, title, description, index }: ReasonCardProps) => {
  const isMobile = useIsMobile();
  const [cardRef, cardVisible] = useIntersectionObserver({ 
    rootMargin: "50px",
    threshold: 0.2 
  });

  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent"
  };

  return (
    <div 
      ref={cardRef}
      className={`bg-card rounded-2xl p-6 md:p-8 shadow-card text-center hover:shadow-xl transition-all duration-700 hover:-translate-y-2 ${
        cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${colorClasses[color as keyof typeof colorClasses]} flex items-center justify-center mx-auto mb-4 md:mb-6`}>
        <Icon className="w-6 h-6 md:w-8 md:h-8" />
      </div>
      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2 md:mb-3">
        {title}
      </h3>
      <p className="text-sm md:text-base text-muted-foreground">
        <span className="hidden md:inline">
          {description.full}
        </span>
        <span className="md:hidden">
          {description.mobile}
        </span>
      </p>
    </div>
  );
};

const WhyChooseUs = () => {
  const isMobile = useIsMobile();
  const [headerRef, headerVisible] = useIntersectionObserver();
  const [imageRef, imageVisible] = useIntersectionObserver({ rootMargin: "50px" });
  const [quoteRef, quoteVisible] = useIntersectionObserver({ rootMargin: "50px" });
  const imageParallax = useScaleParallax({ speed: 0.0002, disabled: isMobile });

  const reasons = [
    {
      icon: Compass,
      color: "primary",
      title: "Experience that understands.",
      description: {
        full: "Founded by two former international students who've been through it all. We know what you need because we've been there.",
        mobile: "Founded by former students who know your needs."
      }
    },
    {
      icon: MessageCircle,
      color: "secondary",
      title: "24/7 support on both sides.",
      description: {
        full: "Our team is always available to help and answer questions, no matter where you or your family are located.",
        mobile: "Always available support for you and family."
      }
    },
    {
      icon: Lock,
      color: "accent",
      title: "Transparency and trust, always.",
      description: {
        full: "Clear pricing, honest communication, and real-time updates keep families informed every step.",
        mobile: "Clear pricing and real-time updates always."
      }
    }
  ];
  
  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div 
          ref={headerRef}
          className={`max-w-3xl mx-auto text-center space-y-3 md:space-y-4 mb-12 md:mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            Why Families Trust{" "}
            <span className="text-secondary">Ace Anchor.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto mb-12 md:mb-16">
          {reasons.map((reason, index) => (
            <ReasonCard key={reason.title} {...reason} index={index} />
          ))}
        </div>

        {/* Community Image - Hidden on mobile */}
        {!isMobile && (
          <div 
            ref={imageRef}
            className={`max-w-4xl mx-auto mb-12 transition-all duration-700 overflow-hidden rounded-3xl ${
              imageVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div ref={imageParallax.ref} style={imageParallax.style}>
              <img
                src={communityImage}
                alt="International student community in Melbourne"
                className="shadow-card w-full h-[400px] object-cover"
              />
            </div>
          </div>
        )}

        {/* Testimonial Quote */}
        <div 
          ref={quoteRef}
          className={`max-w-3xl mx-auto text-center transition-all duration-700 ${
            quoteVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-card rounded-3xl p-6 md:p-12 shadow-card relative">
            <Quote className="h-10 w-10 md:h-12 md:w-12 text-accent/20 absolute top-4 md:top-6 left-4 md:left-6" />
            <p className="text-base md:text-xl text-muted-foreground mb-4 md:mb-6 italic pt-6 md:pt-8">
              <span className="hidden md:inline">
                "Ace Anchor didn't just help me settle in Melbourne, they made me feel like I had family here from Day One. From airport pickup to finding my first job, they were there every step of the way."
              </span>
              <span className="md:hidden">
                "Made me feel like family from Day One in Melbourne."
              </span>
            </p>
            <p className="font-semibold text-foreground">
              Priya S., Monash University
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
